// FoundHerSpotlight.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faStar,
  faGlobe,
  faStarOfLife,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import useFetch from "../../../../hooks/useFetch";

// NOTE: endpoint names follow the same "/foundher/<resource>/<action>" convention
// used in SupportAFoundHer.jsx. Confirm/adjust to match your actual backend routes.
const SPOTLIGHT_LIST_ENDPOINT = "/foundher/spotlight/list";
const SPOTLIGHT_NOMINATE_ENDPOINT = "/foundher/spotlight/nominate";

// Fallback content — shown only when the live spotlight list is empty or the
// fetch fails, so the page never renders blank.
const fallbackSpotlights = [
  {
    id: 1,
    name: "Anika Rao",
    venture: "Saffron & Soul",
    title: "From Kashmir Kitchen to Global Tables",
    category: "Food & Heritage",
    image: "https://picsum.photos/id/1011/800/600",
    founderImage: "https://picsum.photos/id/64/300/300",
    tagline: "Pure Kashmiri saffron & spices with a modern soul",
    story:
      "Anika left her corporate job in Mumbai to revive her grandmother’s 200-year-old saffron farm. Today her premium spice blends are loved in 18 countries.",
    impact: "Empowered 87 women farmers",
    globalReach: "Ships to USA, UAE, UK & Australia",
    likes: 1240,
    loves: 892,
  },
  {
    id: 2,
    name: "Meher Khan",
    venture: "Loop & Loom",
    title: "Turning Waste into Wearable Art",
    category: "Sustainable Fashion",
    image: "https://picsum.photos/id/106/800/600",
    founderImage: "https://picsum.photos/id/65/300/300",
    tagline: "Upcycled fashion that tells a story",
    story:
      "Meher collects leftover sarees from across India and transforms them into contemporary jackets, bags & accessories loved by GenZ globally.",
    impact: "Saved 12 tons of textile waste",
    globalReach: "Featured in Paris & New York Fashion Weeks",
    likes: 980,
    loves: 765,
  },
  {
    id: 3,
    name: "Priya Sharma",
    venture: "Little Earth Explorers",
    title: "Eco-toys rooted in Indian wisdom",
    category: "Education & Toys",
    image: "https://picsum.photos/id/201/800/600",
    founderImage: "https://picsum.photos/id/66/300/300",
    tagline: "Toys that teach sustainability & culture",
    story:
      "A former educator who created wooden toys using Indian mythology and environmental stories. Now exported to 12 countries.",
    impact: "Reached 45,000+ children",
    globalReach: "Available in UK, Germany & Singapore",
    likes: 1450,
    loves: 1120,
  },
];

export default function FoundHerSpotlight() {
  const authState = useSelector((state) => state.authReducer);
  const token = authState?.token;
  const [fetchData] = useFetch();
  const hasFetchedRef = useRef(false);

  const [liveSpotlights, setLiveSpotlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const useFallback = liveSpotlights.length === 0;
  const spotlights = useFallback ? fallbackSpotlights : liveSpotlights;

  const [featured, setFeatured] = useState(null);
  const [interactions, setInteractions] = useState({});

  // Nomination modal state
  const [showNominateModal, setShowNominateModal] = useState(false);
  const [nominateForm, setNominateForm] = useState({
    name: "",
    venture: "",
    story: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pinCode: "",
  });

  const [nominateStatus, setNominateStatus] = useState(""); // "", "submitting", "success", "error"

  useEffect(() => {
    const loadSpotlights = async () => {
      if (hasFetchedRef.current) return;
      setLoading(true);
      try {
        const config = {
          url: SPOTLIGHT_LIST_ENDPOINT,
          method: "get",
          headers: token ? { Authorization: token } : undefined,
        };
        const response = await fetchData(config, { silent: true });
        if (response?.status && response?.records?.length) {
          // Normalize id + field names so keys/likes/loves lookups and JSX
          // rendering work whether it's a live record (founderName/ventureName)
          // or the legacy static fallback shape (name/venture/title/story).
          const normalized = response.records.map((r) => ({
            ...r,
            id: r.id || r._id,
            name: r.founderName,
            venture: r.ventureName,
            title: r.tagline || r.ventureName,
          }));
          setLiveSpotlights(normalized);
          setFeatured(normalized[0]);
        } else {
          setFeatured(fallbackSpotlights[0]);
        }
        hasFetchedRef.current = true;
      } catch (err) {
        console.log(err);
        setFeatured(fallbackSpotlights[0]);
      } finally {
        setLoading(false);
      }
    };
    loadSpotlights();
  }, [token]);

  const handleLike = (id) => {
    setInteractions((prev) => ({
      ...prev,
      [id]: {
        likes:
          (prev[id]?.likes || spotlights.find((s) => s.id === id)?.likes || 0) +
          1,
        loves:
          prev[id]?.loves || spotlights.find((s) => s.id === id)?.loves || 0,
      },
    }));
  };

  const handleLove = (id) => {
    setInteractions((prev) => ({
      ...prev,
      [id]: {
        likes:
          prev[id]?.likes || spotlights.find((s) => s.id === id)?.likes || 0,
        loves:
          (prev[id]?.loves || spotlights.find((s) => s.id === id)?.loves || 0) +
          1,
      },
    }));
  };

  const handleNominateChange = (e) => {
    const { name, value } = e.target;
    setNominateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNominateSubmit = async (e) => {
    e.preventDefault();
    if (!nominateForm.name || !nominateForm.venture || !nominateForm.story)
      return;

    setNominateStatus("submitting");
    try {
      const user = authState?.user || {};
      const config = {
        url: SPOTLIGHT_NOMINATE_ENDPOINT,
        method: "post",
        headers: token ? { Authorization: token } : undefined,
        data: {
          founderName: nominateForm.name,
          ventureName: nominateForm.venture,
          nominationNote: nominateForm.story,
          // Auto-attached, not typed by the requester — pulled straight
          // from their own profile per product decision.
          location: {
            area: nominateForm.area,
            city: nominateForm.city,
            state: nominateForm.state,
            country: nominateForm.country,
            pinCode: nominateForm.pinCode,
          },
        },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        setNominateStatus("success");
        setNominateForm({
          name: "",
          venture: "",
          story: "",
          area: "",
          city: "",
          state: "",
          country: "",
          pinCode: "",
        });
      } else {
        setNominateStatus("error");
      }
    } catch (err) {
      console.log(err);
      setNominateStatus("error");
    }
  };

  if (loading || !featured) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#fff0f5] via-[#f3e8ff] to-[#e0f2fe]">
        <p className="text-violet-500 text-lg">Loading spotlights...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff0f5] via-[#f3e8ff] to-[#e0f2fe]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {/* Hero Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4 bg-white px-6 py-3 rounded-3xl shadow">
            <span className="text-4xl">🌟</span>
            <h1 className="text-5xl md:text-6xl font-black bg-gradient-to-r from-pink-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
              FoundHer Spotlight
            </h1>
          </div>
          <p className="text-xl text-violet-700 max-w-xl mx-auto">
            Celebrating young women founders turning local roots into global
            impact
          </p>
        </div>
        {useFallback && (
          <h1 className="text-xs lg:text-sm font-bold bg-gray-700 px-4 py-2 rounded-lg text-pink-100 flex items-center gap-2 mb-2">
            No live spotlights available yet. Showing sample data for
            demonstration.
          </h1>
        )}
        {/* Main Featured Spotlight */}
        <div className="bg-white rounded-3xl overflow-hidden shadow-2xl mb-16 border border-pink-100">
          <div className="p-8 md:p-12 flex flex-col max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div>
                <h3 className="font-bold text-3xl text-violet-950">
                  {featured.name}
                </h3>
                <p className="text-pink-600">{featured.venture}</p>
              </div>
            </div>

            <h2 className="text-4xl font-bold leading-tight text-violet-950 mt-2">
              {featured.title}
            </h2>
            <p className="text-xl text-pink-600 mt-3 italic">
              “{featured.tagline}”
            </p>

            {featured.spotlightContent ? (
              // Admin-authored rich content — sanitized server-side before
              // storage (see foundherSpotlightController.js adminUpdate).
              <div
                className="mt-8 text-violet-700 leading-relaxed text-lg [&_a]:text-pink-600 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: featured.spotlightContent }}
              />
            ) : (
              <p className="mt-8 text-violet-700 leading-relaxed text-lg">
                {featured.story}
              </p>
            )}

            <div className="mt-10 grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="text-pink-500 font-medium">Impact</div>
                <div className="text-2xl font-bold text-violet-950">
                  {featured.impact}
                </div>
              </div>
              <div>
                <div className="text-pink-500 font-medium">Global Reach</div>
                <div className="text-violet-700">{featured.globalReach}</div>
              </div>
            </div>

            {/* Love & Like Buttons */}
            <div className="flex gap-4 mt-10">
              <button
                onClick={() => handleLove(featured.id)}
                className="flex-1 flex items-center justify-center gap-3 bg-gradient-to-r from-pink-500 to-rose-500 text-white py-5 rounded-2xl hover:brightness-110 transition text-lg font-medium"
              >
                <FontAwesomeIcon icon={faHeart} className="text-2xl" />
                <span>Love</span>
                <span className="font-bold">
                  {interactions[featured.id]?.loves || featured.loves}
                </span>
              </button>

              <button
                onClick={() => handleLike(featured.id)}
                className="flex-1 flex items-center justify-center gap-3 border-2 border-violet-300 hover:bg-violet-50 py-5 rounded-2xl transition text-lg font-medium"
              >
                <FontAwesomeIcon
                  icon={faStar}
                  className="text-2xl text-amber-500"
                />
                <span>Like</span>
                <span className="font-bold">
                  {interactions[featured.id]?.likes || featured.likes}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Other Inspiring Founders */}
        <h2 className="text-3xl font-bold text-violet-950 mb-8 flex items-center gap-3">
          <FontAwesomeIcon icon={faStarOfLife} className="text-pink-500" />
          More Shining Stars
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {spotlights.slice(1).map((spot) => (
            <div
              key={spot.id}
              onClick={() => setFeatured(spot)}
              className="group bg-white border border-pink-100 hover:border-violet-300 rounded-3xl overflow-hidden cursor-pointer transition-all hover:shadow-xl"
            >
              <div className="relative">
                <img
                  src={spot.image}
                  alt={spot.venture}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform"
                />
                <div className="absolute top-4 right-4 bg-white px-4 py-1 rounded-full text-xs font-medium text-pink-600 shadow">
                  {spot.category}
                </div>
              </div>

              <div className="p-6">
                <div className="flex items-center gap-3">
                  <img
                    src={spot.founderImage}
                    alt={spot.name}
                    className="w-12 h-12 rounded-2xl object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-xl">{spot.name}</h4>
                    <p className="text-pink-600 text-sm">{spot.venture}</p>
                  </div>
                </div>

                {spot.spotlightContent ? (
                  <div
                    className="mt-4 text-violet-700 line-clamp-3"
                    dangerouslySetInnerHTML={{ __html: spot.spotlightContent }}
                  />
                ) : (
                  <p className="mt-4 text-violet-700 line-clamp-3">
                    {spot.story}
                  </p>
                )}

                <div className="mt-6 flex justify-between text-sm">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLove(spot.id);
                    }}
                    className="flex items-center gap-2 text-pink-500 hover:text-pink-600"
                  >
                    ❤️ <span>{interactions[spot.id]?.loves || spot.loves}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(spot.id);
                    }}
                    className="flex items-center gap-2 text-amber-500 hover:text-amber-600"
                  >
                    ⭐ <span>{interactions[spot.id]?.likes || spot.likes}</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Final CTA */}
        <div className="mt-20 text-center bg-gradient-to-br from-pink-500 to-violet-600 text-white py-16 rounded-3xl">
          <h3 className="text-3xl font-bold mb-4">
            Know an inspiring woman founder?
          </h3>
          <p className="text-lg opacity-90 mb-8">
            Nominate her to be featured in the next Spotlight
          </p>
          <button
            onClick={() => {
              setNominateStatus("");
              const user = authState?.user || {};
              setNominateForm((prev) => ({
                ...prev,
                area: user.area || "",
                city: user.city || "",
                state: user.state || "",
                country: user.country || "",
                pinCode: user.pinCode || user.pincode || "",
              }));
              setShowNominateModal(true);
            }}
            className="px-12 py-5 bg-white text-pink-600 font-semibold rounded-2xl text-lg hover:bg-pink-50 transition"
          >
            Nominate a Founder →
          </button>
        </div>
      </div>

      {/* Nomination Modal */}
      {showNominateModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setShowNominateModal(false)}
              className="absolute top-4 right-4 w-fit p-1 text-violet-400 hover:text-violet-700"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            <h3 className="text-xl font-bold text-violet-950 mb-1">
              Nominate a Founder
            </h3>
            <p className="text-sm text-violet-600 mb-5">
              Tell us about an inspiring woman founder we should feature next.
            </p>

            {nominateStatus === "success" ? (
              <div className="text-center py-6">
                <p className="text-2xl mb-2">🎉</p>
                <p className="text-violet-800 font-medium">
                  Thank you! Your nomination has been submitted.
                </p>
                <button
                  onClick={() => setShowNominateModal(false)}
                  className="mt-6 px-6 py-2 bg-pink-500 text-white rounded-xl hover:bg-pink-600 transition"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleNominateSubmit} className="space-y-3">
                <input
                  required
                  name="name"
                  value={nominateForm.name}
                  onChange={handleNominateChange}
                  placeholder="Founder's name"
                  className="w-full px-4 py-3 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                />
                <input
                  required
                  name="venture"
                  value={nominateForm.venture}
                  onChange={handleNominateChange}
                  placeholder="Venture / business name"
                  className="w-full px-4 py-3 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                />
                <textarea
                  required
                  name="story"
                  maxLength={2000}
                  value={nominateForm.story}
                  onChange={handleNominateChange}
                  placeholder="Why does she deserve the spotlight?"
                  className="w-full px-4 py-3 rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm h-28 resize-y"
                />
                <p className="text-xs text-violet-400 text-right -mt-2">
                  {nominateForm.story.length}/2000
                </p>

                <div>
                  <p className="text-xs font-medium text-violet-500 mb-1">
                    Founder's location
                    <span className="text-violet-300 font-normal">
                      {" "}
                      — defaults to your own, edit if she's elsewhere
                    </span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      name="area"
                      value={nominateForm.area}
                      onChange={handleNominateChange}
                      placeholder="Area"
                      className="w-full px-4 py-2.5 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                    />
                    <input
                      name="city"
                      value={nominateForm.city}
                      onChange={handleNominateChange}
                      placeholder="City"
                      className="w-full px-4 py-2.5 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                    />
                    <input
                      name="state"
                      value={nominateForm.state}
                      onChange={handleNominateChange}
                      placeholder="State"
                      className="w-full px-4 py-2.5 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                    />
                    <input
                      name="country"
                      value={nominateForm.country}
                      onChange={handleNominateChange}
                      placeholder="Country"
                      className="w-full px-4 py-2.5 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                    />
                    <input
                      name="pinCode"
                      value={nominateForm.pinCode}
                      onChange={handleNominateChange}
                      placeholder="Postal / PIN Code"
                      className="col-span-2 w-full px-4 py-2.5 text-black rounded-xl border border-pink-200 focus:border-pink-400 focus:outline-none text-sm"
                    />
                  </div>
                </div>

                {nominateStatus === "error" && (
                  <p className="text-xs text-red-500">
                    Something went wrong submitting your nomination. Please try
                    again.
                  </p>
                )}

                <button
                  type="submit"
                  disabled={nominateStatus === "submitting"}
                  className="w-full py-3 bg-gradient-to-r from-pink-500 to-violet-500 text-white rounded-xl font-semibold hover:brightness-110 transition disabled:opacity-60"
                >
                  {nominateStatus === "submitting"
                    ? "Submitting..."
                    : "Submit Nomination"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
